import React, { useEffect, useMemo, useRef, useState } from 'react'
import { Button, Switch, Tooltip } from 'antd'
import { FlagOutlined, SettingOutlined } from '@ant-design/icons'
import { Stack, Typography } from '@sureco/design-system'
import { Table, TableProps } from '@surecompanies/core_components'
import ContentSection from 'layouts/ContentSection/ContentSection'
import useCustomerConfigurationInventory from './hooks/useCustomerConfigurationInventory'
import type {
  CustomerConfigurationInventoryItem,
  DefinitionManifestEntry,
} from './hooks/useCustomerConfigurationInventory'
import ToggleFlagModal from './components/ToggleFlagModal'
import EditConfigurationModal from './components/EditConfigurationModal'
import CreateConfigurationPickerModal from './components/CreateConfigurationPickerModal'

type ConfigurationTabProps = {
  id: string
}

type SummaryTileProps = {
  label: string
  value: number
  description: string
}

type ActiveModal = 'toggle' | 'config' | 'create' | null

function TypeIcon({ item }: { item: CustomerConfigurationInventoryItem }) {
  if (item.type === 'feature-flag') {
    return (
      <Tooltip title="Feature flag">
        <FlagOutlined style={{ color: '#2563EB', fontSize: 16 }} />
      </Tooltip>
    )
  }

  if (item.type === 'configuration') {
    return (
      <Tooltip title="Configuration">
        <SettingOutlined style={{ color: '#7C3AED', fontSize: 16 }} />
      </Tooltip>
    )
  }

  return (
    <Tooltip title="System only — no schema">
      <span style={{ color: '#C0C0C0', fontSize: 16 }}>—</span>
    </Tooltip>
  )
}

function SummaryTile({ label, value, description }: SummaryTileProps) {
  return (
    <Stack
      space={8}
      padding={16}
      borderRadius={8}
      style={{ background: '#E0EAF1', minHeight: '100%' }}
    >
      <Typography size={14} color="#5B7083">
        {label}
      </Typography>
      <Typography size={28} bold color="#103044">
        {value}
      </Typography>
      <Typography size={13} color="#5B7083">
        {description}
      </Typography>
    </Stack>
  )
}

function renderDescriptionCell(description: string | null) {
  if (description === null || description.length === 0) {
    return (
      <Typography size={14} color="#5B7083">
        -
      </Typography>
    )
  }

  return (
    <div
      style={{
        color: '#103044',
        fontSize: 14,
        lineHeight: '20px',
        maxWidth: 420,
        minWidth: 260,
        whiteSpace: 'normal',
        wordBreak: 'break-word',
      }}
    >
      {description}
    </div>
  )
}

export default function ConfigurationTab({ id }: ConfigurationTabProps) {
  const { data, isLoading, isError, error } =
    useCustomerConfigurationInventory(id)

  const [activeModal, setActiveModal] = useState<ActiveModal>(null)
  const [editingItem, setEditingItem] =
    useState<CustomerConfigurationInventoryItem | null>(null)
  const [creatingDefinition, setCreatingDefinition] =
    useState<DefinitionManifestEntry | null>(null)

  // Ant Design's `afterClose` fires after a ~300ms close animation. By that
  // time the user may have already opened a different modal that reuses the
  // same state slots (`editingItem` / `creatingDefinition`). We mirror
  // `activeModal` in a ref so each `afterClose` handler can see the live
  // value and skip the cleanup when another flow has taken over.
  const activeModalRef = useRef<ActiveModal>(activeModal)
  useEffect(() => {
    activeModalRef.current = activeModal
  }, [activeModal])

  const inventory = data ?? {
    items: [],
    featureFlags: [],
    configurations: [],
    systemOnly: [],
    availableDefinitions: [],
  }

  function openToggleModal(item: CustomerConfigurationInventoryItem) {
    setCreatingDefinition(null)
    setEditingItem(item)
    setActiveModal('toggle')
  }

  function openEditModal(item: CustomerConfigurationInventoryItem) {
    setCreatingDefinition(null)
    setEditingItem(item)
    setActiveModal('config')
  }

  function closeModal() {
    setActiveModal(null)
  }

  function clearEditingItemIfIdle() {
    if (activeModalRef.current === null) {
      setEditingItem(null)
    }
  }

  function clearCreatingDefinitionIfIdle() {
    if (activeModalRef.current === null) {
      setCreatingDefinition(null)
    }
  }

  // The picker is the entry point into the create flow, so its `afterClose`
  // must clean up both transient slots. Clearing only `editingItem` would
  // leave a stale create `EditConfigurationModal` mounted with `open={false}`
  // if `creatingDefinition` was set during the picker's lifecycle.
  function clearTransientSelectionsIfIdle() {
    if (activeModalRef.current === null) {
      setEditingItem(null)
      setCreatingDefinition(null)
    }
  }

  function handleSelectConfig(definition: DefinitionManifestEntry) {
    setEditingItem(null)
    setCreatingDefinition(definition)
    setActiveModal('config')
  }

  // Clear both editing slots up-front so an in-flight edit/create modal that
  // is still mid-close-animation cannot stay mounted with `open={false}` while
  // the picker is showing. Without this, the picker's `afterClose` would later
  // be the one to unmount it, silently discarding the hidden form state.
  function openCreatePicker() {
    setEditingItem(null)
    setCreatingDefinition(null)
    setActiveModal('create')
  }

  const noAvailableDefinitions = inventory.availableDefinitions.length === 0

  const columns = useMemo<
    NonNullable<TableProps<CustomerConfigurationInventoryItem>['columns']>
  >(
    () => [
      {
        title: '',
        dataIndex: 'type',
        key: 'type',
        width: 44,
        align: 'center' as const,
        sorter: (left, right) =>
          (left.type ?? '').localeCompare(right.type ?? ''),
        render: (_: unknown, item: CustomerConfigurationInventoryItem) => (
          <TypeIcon item={item} />
        ),
      },
      {
        title: 'Name',
        dataIndex: 'title',
        key: 'title',
        sorter: (left, right) => left.title.localeCompare(right.title),
        render: (title: string, item: CustomerConfigurationInventoryItem) => (
          <Stack space={4}>
            <Typography size={14} bold color="#103044">
              {title}
            </Typography>
            <span
              style={{
                background: '#EDF2F7',
                borderRadius: 4,
                color: '#5B7083',
                fontFamily: 'monospace',
                fontSize: 11,
                padding: '1px 6px',
                display: 'inline-block',
              }}
            >
              {item.key}
            </span>
          </Stack>
        ),
      },
      {
        title: 'Category',
        dataIndex: 'category',
        key: 'category',
        sorter: (left, right) =>
          (left.category ?? '').localeCompare(right.category ?? ''),
        render: (category: string | null) =>
          category ? (
            <Typography size={14} color="#103044">
              {category}
            </Typography>
          ) : (
            <Typography size={14} color="#5B7083">
              -
            </Typography>
          ),
      },
      {
        title: 'Description',
        dataIndex: 'description',
        key: 'description',
        sorter: (left, right) =>
          (left.description ?? '').localeCompare(right.description ?? ''),
        render: (description: string | null) =>
          renderDescriptionCell(description),
      },
      {
        title: 'Actions',
        key: 'actions',
        width: 100,
        render: (_: unknown, item: CustomerConfigurationInventoryItem) => {
          if (!item.isEditable) {
            return (
              <Tooltip title="No schema defined — read-only">
                <Typography size={12} color="#C0C0C0">
                  Read-only
                </Typography>
              </Tooltip>
            )
          }

          if (item.type === 'feature-flag') {
            return (
              <Tooltip
                title={
                  item.value === true ? 'Click to disable' : 'Click to enable'
                }
              >
                <Switch
                  checked={item.value === true}
                  onChange={() => openToggleModal(item)}
                  size="small"
                />
              </Tooltip>
            )
          }

          return (
            <button
              onClick={() => openEditModal(item)}
              style={{
                background: 'none',
                border: '1px solid #D1D5DB',
                borderRadius: 6,
                color: '#103044',
                cursor: 'pointer',
                fontSize: 12,
                padding: '4px 10px',
              }}
            >
              Edit
            </button>
          )
        },
      },
    ],
    []
  )

  if (isLoading) {
    return (
      <ContentSection title="Configuration" loading>
        <></>
      </ContentSection>
    )
  }

  if (isError) {
    return (
      <ContentSection title="Configuration">
        <Stack space={8}>
          <Typography size={16} bold color="#103044">
            Unable to load customer configuration values.
          </Typography>
          <Typography size={14} color="#5B7083">
            {error.message}
          </Typography>
        </Stack>
      </ContentSection>
    )
  }

  const addButton = (
    <Tooltip
      title={
        noAvailableDefinitions
          ? 'All schema-backed configurations are already defined for this customer.'
          : ''
      }
    >
      {/*
        AntD applies `pointer-events: none` to a disabled Button, which prevents
        the Tooltip from receiving hover events. Wrap in an inline-block span so
        the Tooltip's trigger sits on a non-disabled element and the disabled
        explanation actually appears.
      */}
      <span style={{ display: 'inline-block' }}>
        <Button
          type="primary"
          disabled={noAvailableDefinitions}
          onClick={openCreatePicker}
        >
          + Add configuration
        </Button>
      </span>
    </Tooltip>
  )

  return (
    <>
      <Stack space={20}>
        <ContentSection title="Configuration Overview">
          <div
            style={{
              display: 'grid',
              gap: 16,
              gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
            }}
          >
            <SummaryTile
              label="Defined values"
              value={inventory.items.length}
              description="All currently stored keys for this customer."
            />
            <SummaryTile
              label="Feature flags"
              value={inventory.featureFlags.length}
              description="Schema-backed boolean flags already defined."
            />
            <SummaryTile
              label="Configurations"
              value={inventory.configurations.length}
              description="Schema-backed object configurations already defined."
            />
            <SummaryTile
              label="System only"
              value={inventory.systemOnly.length}
              description="Stored values without a registered schema."
            />
          </div>
        </ContentSection>

        <ContentSection title="Customer Configurations" actions={addButton}>
          <Stack space={16}>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 20,
                flexWrap: 'wrap',
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <FlagOutlined style={{ color: '#2563EB', fontSize: 15 }} />
                <Typography size={13} color="#5B7083">
                  Feature flag
                </Typography>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <SettingOutlined style={{ color: '#7C3AED', fontSize: 15 }} />
                <Typography size={13} color="#5B7083">
                  Configuration
                </Typography>
              </div>
              <Typography size={13} color="#9CA3AF">
                · Schema-backed rows are editable. System-only rows are
                read-only.
              </Typography>
            </div>
            <Table<CustomerConfigurationInventoryItem>
              bordered
              columns={columns}
              dataSource={inventory.items}
              locale={{
                emptyText:
                  'No configuration values are currently defined for this customer.',
              }}
              pagination={{
                hideOnSinglePage: true,
                pageSize: 10,
                position: ['bottomCenter'],
                showSizeChanger: false,
              }}
              rowKey={(item) => item.id}
              size="small"
            />
          </Stack>
        </ContentSection>
      </Stack>

      <ToggleFlagModal
        open={activeModal === 'toggle'}
        item={editingItem}
        customerId={id}
        onClose={closeModal}
        onAfterClose={clearEditingItemIfIdle}
      />

      {editingItem !== null && (
        <EditConfigurationModal
          mode="edit"
          open={activeModal === 'config'}
          item={editingItem}
          customerId={id}
          onClose={closeModal}
          onAfterClose={clearEditingItemIfIdle}
        />
      )}

      {creatingDefinition !== null && (
        <EditConfigurationModal
          mode="create"
          open={activeModal === 'config'}
          definition={creatingDefinition}
          customerId={id}
          onClose={closeModal}
          onAfterClose={clearCreatingDefinitionIfIdle}
        />
      )}

      <CreateConfigurationPickerModal
        open={activeModal === 'create'}
        customerId={id}
        availableDefinitions={inventory.availableDefinitions}
        onClose={closeModal}
        onAfterClose={clearTransientSelectionsIfIdle}
        onSelectConfig={handleSelectConfig}
      />
    </>
  )
}
